import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronRight, 
  Check, 
  Clock, 
  Package, 
  Truck, 
  CheckCircle, 
  XCircle,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_CONFIG = {
  placed: {
    label: 'Order Placed',
    color: 'bg-blue-500',
    icon: Clock,
    next: ['confirmed', 'cancelled']
  },
  confirmed: {
    label: 'Confirmed',
    color: 'bg-green-500',
    icon: Check,
    next: ['preparing', 'packing', 'scheduled', 'cancelled']
  },
  preparing: {
    label: 'Preparing',
    color: 'bg-amber-500',
    icon: Package,
    next: ['ready']
  },
  packing: {
    label: 'Packing',
    color: 'bg-amber-500',
    icon: Package,
    next: ['ready_for_pickup']
  },
  scheduled: {
    label: 'Scheduled',
    color: 'bg-purple-500',
    icon: Clock,
    next: ['in_progress']
  },
  in_progress: {
    label: 'In Progress',
    color: 'bg-amber-500',
    icon: Package,
    next: ['completed']
  },
  ready: {
    label: 'Ready',
    color: 'bg-purple-500',
    icon: Package,
    next: ['picked_up']
  },
  ready_for_pickup: {
    label: 'Ready for Pickup',
    color: 'bg-purple-500',
    icon: Package,
    next: ['picked_up']
  },
  picked_up: {
    label: 'Picked Up',
    color: 'bg-cyan-500',
    icon: Truck,
    next: ['in_transit']
  },
  in_transit: {
    label: 'In Transit',
    color: 'bg-blue-500',
    icon: Truck,
    next: ['delivered']
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-500',
    icon: CheckCircle,
    next: ['closed']
  },
  closed: {
    label: 'Closed',
    color: 'bg-green-600',
    icon: CheckCircle,
    next: []
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-green-500',
    icon: CheckCircle,
    next: []
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-500',
    icon: XCircle,
    next: []
  }
};

export default function OrderWorkflowProgress({ order, onStatusChange }) {
  const [transitioning, setTransitioning] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [notes, setNotes] = useState('');

  const currentStatus = order.status || 'placed';
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.placed;
  const possibleNextStates = statusConfig.next || [];

  const handleTransitionClick = (toStatus) => {
    // Show confirmation for cancellation
    if (toStatus === 'cancelled') {
      setTargetStatus(toStatus);
      setShowConfirmDialog(true);
    } else {
      performTransition(toStatus);
    }
  };

  const performTransition = async (toStatus, transitionNotes = '') => {
    setTransitioning(true);
    setShowConfirmDialog(false);

    try {
      const response = await api.post(`/vendor/orders/${order.id}/transition`, {
        to_status: toStatus,
        notes: transitionNotes || notes || undefined,
      });

      toast.success(`Order status updated to ${STATUS_CONFIG[toStatus]?.label || toStatus}`);
      
      if (onStatusChange) {
        onStatusChange(response.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update order status';
      toast.error(errorMsg);
      console.error('Transition error:', error);
    } finally {
      setTransitioning(false);
      setNotes('');
      setTargetStatus(null);
    }
  };

  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-4">
      {/* Current Status Badge */}
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${statusConfig.color} text-white`}>
          <StatusIcon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Current Status</p>
          <p className="text-lg font-semibold">{statusConfig.label}</p>
        </div>
      </div>

      {/* Workflow Actions */}
      {possibleNextStates.length > 0 && (
        <div className="border rounded-lg p-4 bg-muted/30">
          <p className="text-sm font-medium mb-3">Move order to next stage:</p>
          <div className="flex flex-wrap gap-2">
            {possibleNextStates.map((nextStatus) => {
              const nextConfig = STATUS_CONFIG[nextStatus];
              const NextIcon = nextConfig.icon;
              const isCancellation = nextStatus === 'cancelled';

              return (
                <Button
                  key={nextStatus}
                  variant={isCancellation ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => handleTransitionClick(nextStatus)}
                  disabled={transitioning}
                  className="gap-2"
                  data-testid={`transition-to-${nextStatus}`}
                >
                  {transitioning ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <NextIcon className="h-4 w-4" />
                  )}
                  {nextConfig.label}
                  {!isCancellation && <ChevronRight className="h-3 w-3" />}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Final Status Indicator */}
      {possibleNextStates.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <p className="text-sm text-muted-foreground">
            This order is in a final state and cannot be modified
          </p>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm Order Cancellation
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">
                Cancellation Reason (optional)
              </label>
              <Textarea
                placeholder="Enter reason for cancellation..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="cancellation-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                setNotes('');
                setTargetStatus(null);
              }}
            >
              Keep Order
            </Button>
            <Button
              variant="destructive"
              onClick={() => performTransition('cancelled', notes)}
              disabled={transitioning}
              data-testid="confirm-cancellation"
            >
              {transitioning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Yes, Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
